import {
    LARGEST_TRADE_FLOOR_PCT_AUM,
    optimizerDynamicConstraintKey,
} from '../../utils/optimizerConstraintEditor';
import { optimizerGroupIntentKey } from '../../utils/optimizerGenericGroupContract';

// One definition per supported rule. `key` is the editor field held by
// useOptimizer; `wireName` is the record the run command carries (see
// optimizerConstraintEditor). Labels describe what the solver actually limits:
// every "% of GMV budget" limit divides by the configured GMV budget, not by the
// portfolio's realized GMV.
export const PORTFOLIO_RULES = Object.freeze([
    Object.freeze({
        key: 'gmv',
        wireName: 'gross_market_value_musd',
        label: 'Gross exposure (GMV)',
        unit: 'usd',
        unitLabel: 'USD',
        form: 'max',
        alwaysApplied: true,
        description: 'Sum of absolute position values stays at or below this budget. '
            + 'Always applied; every "% of GMV budget" limit is measured against it.',
    }),
    Object.freeze({
        key: 'nmv_pct_gmv',
        wireName: 'net_market_value_abs_pct_gmv',
        label: 'Net exposure (NMV)',
        unit: 'percent',
        unitLabel: '% of GMV budget',
        form: 'abs_max',
        description: 'Net market value stays within ± this share of the GMV budget.',
    }),
    Object.freeze({
        key: 'position_gmv_pct_of_aum',
        wireName: 'position_abs_pct_aum',
        label: 'Largest position',
        unit: 'percent',
        unitLabel: '% of AUM',
        form: 'max',
        description: 'Every position\'s absolute value stays at or below this share of AUM.',
    }),
    Object.freeze({
        key: 'beta_exp_as_pct_of_aum',
        wireName: 'beta_exposure_abs_pct_aum',
        label: 'Beta exposure',
        unit: 'percent',
        unitLabel: '% of AUM',
        form: 'abs_max',
        description: 'Portfolio beta exposure stays within ± this share of AUM.',
    }),
    Object.freeze({
        key: 'factor_risk_pct_of_total',
        wireName: 'factor_vol_pct_aum',
        label: 'Factor volatility',
        unit: 'percent',
        unitLabel: '% of AUM',
        form: 'max',
        description: 'Volatility from factor exposures stays at or below this share of AUM. '
            + 'This is not the factor share of total variance.',
    }),
    Object.freeze({
        key: 'port_vol_pct_aum',
        wireName: 'portfolio_vol_pct_aum',
        label: 'Total volatility',
        unit: 'percent',
        unitLabel: '% of AUM',
        form: 'max',
        description: 'Total portfolio volatility (factor plus specific) stays at or below '
            + 'this share of AUM.',
    }),
    Object.freeze({
        key: 'ticker_gmv_pct_delta',
        wireName: 'max_trade_abs_pct_existing_position',
        label: 'Largest trade',
        unit: 'percent',
        unitLabel: '% of existing position',
        form: 'max',
        description: 'Each trade stays at or below this share of the existing position. '
            + `Small positions count as at least ${LARGEST_TRADE_FLOOR_PCT_AUM}% of AUM, `
            + 'so they can still be resized, and names not yet held can be opened.',
    }),
]);

// Liquidity and turnover limits (Max Alpha only). ADV is the name's average
// daily dollar volume: shares traded per day × price.
export const TRADING_RULES = Object.freeze([
    Object.freeze({
        key: 'turnover_pct_gmv',
        wireName: 'max_turnover_pct_gmv',
        ruleId: 'trading:max_turnover_pct_gmv',
        label: 'Turnover',
        unit: 'percent',
        unitLabel: '% of GMV budget',
        form: 'max',
        description: 'Total trading, buys plus sells, stays at or below this share of '
            + 'the GMV budget.',
    }),
    Object.freeze({
        key: 'position_days_adv',
        wireName: 'max_position_days_adv',
        ruleId: 'trading:max_position_days_adv',
        label: 'Position liquidity',
        unit: 'days',
        unitLabel: 'days of daily volume',
        form: 'max',
        description: 'Every position stays at or below this many days of the name\'s '
            + 'average daily dollar volume.',
    }),
    Object.freeze({
        key: 'trade_pct_adv',
        wireName: 'max_trade_pct_adv',
        ruleId: 'trading:max_trade_pct_adv',
        label: 'Trade liquidity',
        unit: 'percent',
        unitLabel: '% of daily volume',
        form: 'max',
        description: 'Every trade stays at or below this share of the name\'s average '
            + 'daily dollar volume.',
    }),
]);

// Per-name controls are set in the securities table; each is either respected
// or breached.
export const SECURITY_CONTROL_RULES = Object.freeze([
    Object.freeze({
        control: 'hold',
        ruleId: 'controls:hold',
        label: 'Held names',
        optionLabel: 'Hold',
        unit: 'count',
        form: 'max',
        description: 'Held names keep their baseline position exactly.',
    }),
    Object.freeze({
        control: 'no_short',
        ruleId: 'controls:no_short',
        label: 'No-short names',
        optionLabel: 'No short',
        unit: 'count',
        form: 'max',
        description: 'No-short names end long or flat.',
    }),
]);

export const CARDINALITY_RULE = Object.freeze({
    key: 'max_names_changed',
    wireName: 'max_names_changed',
    ruleId: 'cardinality:max_names_changed',
    label: 'Maximum names changed',
    unit: 'count',
    unitLabel: 'selected names',
    form: 'max',
    description: 'Counts selected names whose optimized position differs from the chosen baseline.',
});

export const SEARCHABLE_PORTFOLIO_RULES = Object.freeze([
    ...PORTFOLIO_RULES.filter((rule) => !rule.alwaysApplied),
    ...TRADING_RULES,
    CARDINALITY_RULE,
]);

export const FACTOR_RULE = Object.freeze({
    name: 'factor_vol_bps_gmv',
    label: 'Standalone factor volatility',
    unit: 'bps',
    unitLabel: 'bps of GMV budget',
    form: 'max',
    defaultValue: 10,
    description: 'Factor exposure × factor volatility, as a magnitude, relative to the GMV '
        + 'budget. This is the quantity a factor limit constrains.',
});

export const ATTRIBUTE_RULE_METRICS = Object.freeze([
    Object.freeze({
        name: 'group_beta_exposure_abs_pct_gmv',
        label: 'Beta exposure',
        unit: 'percent',
        unitLabel: '% of GMV budget',
        form: 'abs_max',
    }),
    Object.freeze({
        name: 'group_net_market_value_abs_pct_gmv',
        label: 'Net exposure (NMV)',
        unit: 'percent',
        unitLabel: '% of GMV budget',
        form: 'abs_max',
    }),
]);

export const ATTRIBUTE_RULE_DEFAULT_VALUE = 10;

// Input precision per unit: limits such as 0.35% of AUM must be enterable.
export const RULE_INPUT_DECIMALS = Object.freeze({
    usd: 0,
    percent: 4,
    bps: 2,
    count: 0,
    days: 2,
    ratio: 3,
});

const VALUE_FORMATS = Object.freeze({
    usd: { maximumFractionDigits: 0 },
    percent: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    bps: { minimumFractionDigits: 1, maximumFractionDigits: 1 },
    count: { maximumFractionDigits: 0 },
    days: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
});

export const portfolioRuleId = (rule) => rule.ruleId ?? `static:${rule.wireName}`;
export const factorRuleId = (factor) => `factor:${FACTOR_RULE.name}:${factor}`;
export const factorRuleKey = (factor) => optimizerDynamicConstraintKey(factor, 'Factor');

export function attributeMetricByName(name) {
    return ATTRIBUTE_RULE_METRICS.find((metric) => metric.name === name) ?? null;
}

export function formatRuleValue(value, unit) {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    const number = Number(value);
    const text = Math.abs(number).toLocaleString(
        'en-US',
        VALUE_FORMATS[unit] ?? VALUE_FORMATS.percent,
    );
    const sign = number < 0 ? '-' : '';
    if (unit === 'usd') return `${sign}$${text}`;
    if (unit === 'bps') return `${sign}${text} bps`;
    if (unit === 'count') return `${sign}${text}`;
    if (unit === 'days') return `${sign}${text} days`;
    return `${sign}${text}%`;
}

export function formatRuleLimit(value, { unit, form }) {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    return `${form === 'abs_max' ? '±' : form === 'min' ? '≥' : '≤'} ${formatRuleValue(value, unit)}`;
}

// A distance between two values of one rule: percentage points for percent
// rules, never a relative change.
export function formatRuleDifference(value, unit) {
    const magnitude = Math.abs(Number(value));
    if (!Number.isFinite(magnitude)) return '—';
    if (unit === 'percent') return `${magnitude.toFixed(2)} pp`;
    if (unit === 'bps') return `${magnitude.toFixed(1)} bps`;
    if (unit === 'days') return `${magnitude.toFixed(2)} days`;
    return `$${magnitude.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

// Where the optimized value sits relative to the run's limit. Limit usage is
// only meaningful for a nonnegative upper bound; a signed interval reports
// its distance to the nearest bound instead.
export function describeRuleResult(evaluation, { unit, form, control = null }) {
    if (!evaluation?.configured) return null;
    if (!evaluation.applied) return { headroom: 'Not applied in this run', usage: null };
    if (control) {
        const breaches = evaluation.detail?.breaches?.length ?? 0;
        return {
            headroom: breaches
                ? `${breaches} controlled name${breaches === 1 ? '' : 's'} breached`
                : 'Every controlled name is respected',
            usage: null,
        };
    }
    const opened = evaluation.detail?.unheld_names_opened?.length ?? 0;
    if (opened) {
        return {
            headroom: `Opens ${opened} name${opened === 1 ? '' : 's'} with no existing position`,
            usage: null,
        };
    }
    const { limit, optimized } = evaluation;
    if (limit == null || optimized == null) return null;
    const slack = form === 'min' ? optimized - limit : limit - (form === 'abs_max' ? Math.abs(optimized) : optimized);
    const headroom = evaluation.status === 'exceeds'
        ? `${form === 'min' ? 'Below the minimum' : 'Exceeds the limit'} by ${formatRuleDifference(slack, unit)}`
        : evaluation.status === 'at_limit'
            ? 'At the limit'
            : `${formatRuleDifference(slack, unit)} ${form === 'abs_max'
                ? 'inside the nearest bound'
                : form === 'min' ? 'above the minimum' : 'below the limit'}`;
    const usage = form === 'max' && limit > 0
        ? `Uses ${Math.round((optimized / limit) * 100)}% of the limit`
        : null;
    return { headroom, usage };
}

const sameNumber = (left, right) => {
    if (left == null || right == null) return left == null && right == null;
    const a = Number(left);
    const b = Number(right);
    return Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));
};

// Compare the run's rule with the editor's current record. A changed rule's
// displayed status belongs to the previous run, so it must be rerun before it
// can be checked; its edited limit is drawn as a draft.
export function ruleEditState(evaluation, current) {
    if (!evaluation) return { changed: false, draftLimit: null };
    // Explore endpoints configure a future search. The displayed result is
    // evaluated against its receipt's tested point, never the hidden Fixed value.
    if (current?.enabled && current.explore) return { changed: false, draftLimit: null };
    const configured = current != null;
    const enabled = configured && current.enabled === true;
    const limit = configured ? current.value ?? null : null;
    const changed = evaluation.configured !== configured || (
        configured && (evaluation.enabled !== enabled || !sameNumber(evaluation.limit, limit))
    );
    return {
        changed,
        draftLimit: changed && limit != null && Number.isFinite(Number(limit))
            ? Number(limit)
            : null,
    };
}

// Join a receipt-bound evaluation to editor records. Group rules are keyed by
// the same intent identity the editor uses, not the server's digest.
export function indexConstraintEvaluation(evaluation) {
    const byRuleId = new Map();
    const byGroupIntentKey = new Map();
    (evaluation?.rules ?? []).forEach((rule) => {
        byRuleId.set(rule.rule_id, rule);
        if (rule.scope === 'attribute') {
            byGroupIntentKey.set(optimizerGroupIntentKey({
                attribute_definition_id: rule.identity.attribute_definition_id,
                group_kind: rule.identity.group_kind,
                group_value_key: rule.identity.group_value_key ?? null,
                name: rule.name,
            }), rule);
        }
    });
    return { byRuleId, byGroupIntentKey, portfolio: evaluation?.portfolio ?? {}, hasResult: evaluation?.receipt_id != null };
}

export function baselineColumnLabel(evaluation, viewMode) {
    const mode = evaluation?.baseline_source_mode
        ?? (viewMode === 'pos-sizing' ? 'pos_sizing' : 'actual');
    return mode === 'pos_sizing' ? 'Baseline (Pos Sizing)' : 'Baseline (Current)';
}

export function attributeGroupLabel(record, attributeDefinitions = []) {
    const definition = attributeDefinitions.find((candidate) => (
        candidate.attribute_definition_id === record.attribute_definition_id
    ));
    const group = (definition?.groups ?? []).find((candidate) => (
        candidate.group_kind === record.group_kind
        && (candidate.group_value_key ?? null) === (record.group_value_key ?? null)
    ));
    return {
        attributeName: definition?.display_name ?? 'Attribute',
        categoryLabel: record.group_kind === 'unassigned'
            ? 'Unassigned'
            : (group?.group_value ?? record.group_value_key ?? 'Category'),
    };
}

// Every enabled rule across scopes, read from the same editor state the scope
// views edit. The GMV budget is always applied.
export function listActiveRules({
    constraints,
    constraintToggles,
    groupConstraints = [],
    attributeDefinitions = [],
}) {
    const staticValues = constraints?.static_constraints ?? {};
    const staticToggles = constraintToggles?.static_constraints ?? {};
    const portfolio = [...PORTFOLIO_RULES, ...TRADING_RULES]
        .filter((rule) => rule.alwaysApplied || staticToggles[rule.key] === true)
        .map((rule) => ({
            scope: 'portfolio',
            id: portfolioRuleId(rule),
            rule,
            label: rule.label,
            value: staticValues[rule.key] ?? null,
        }));
    if (Object.prototype.hasOwnProperty.call(staticToggles, CARDINALITY_RULE.key)
        && staticToggles[CARDINALITY_RULE.key] === true) {
        portfolio.push({
            scope: 'portfolio',
            id: portfolioRuleId(CARDINALITY_RULE),
            rule: CARDINALITY_RULE,
            label: CARDINALITY_RULE.label,
            value: staticValues[CARDINALITY_RULE.key] ?? null,
        });
    }
    const factors = Object.values(constraints?.dynamic_constraints ?? {})
        .filter((record) => record?.enabled === true && record?.factor_name)
        .sort((left, right) => left.factor_name.localeCompare(right.factor_name))
        .map((record) => ({
            scope: 'factor',
            id: factorRuleId(record.factor_name),
            rule: FACTOR_RULE,
            factor: record.factor_name,
            label: record.factor_name,
            value: record.value ?? null,
        }));
    const attributes = groupConstraints
        .filter((record) => record?.enabled === true)
        .map((record) => {
            const metric = attributeMetricByName(record.name);
            const { attributeName, categoryLabel } = attributeGroupLabel(
                record,
                attributeDefinitions,
            );
            return {
                scope: 'attribute',
                id: optimizerGroupIntentKey(record),
                rule: metric,
                record,
                label: `${attributeName}: ${categoryLabel}`,
                value: record.value ?? null,
            };
        });
    return [...portfolio, ...factors, ...attributes];
}
